import React, { FC, useLayoutEffect, useRef } from 'react'
import Layout from 'src/components/layout'
import NavigableSection, {
  INavigableSectionProps,
} from 'src/components/sections/NavigableSection'
import HeroSection from 'src/components/sections/hero'
import SideNavigation from 'src/components/layout/side-navigation'
import { gsap, ScrollTrigger } from 'gsap/all'
import { createShowFromBottomAnimation } from 'src/helpers/animations'
import ContactSection from 'src/components/sections/ContactSection'

if (typeof window !== 'undefined') {
  gsap.registerPlugin(ScrollTrigger)
}

interface IMetaProp {
  name?: string
  content?: string
}

interface INavigablePageProps {
  title: string
  metas: IMetaProp[]
  titlePart1: string
  titlePart2: string
  sections: INavigableSectionProps[]
  showIso?: boolean
}

const NavigablePage: FC<INavigablePageProps> = ({
  title,
  metas,
  titlePart1,
  titlePart2,
  sections,
  showIso,
}) => {
  const timeline = useRef<any>(null)

  useLayoutEffect(() => {
    if (typeof window !== 'undefined') {
      // Kill existing triggers for reveal items to prevent duplicates and stale states
      ScrollTrigger.getAll().forEach(trigger => {
        if (trigger.trigger && (trigger.trigger as Element).classList.contains('reveal-item')) {
          trigger.kill();
        }
      });

      const tl = gsap.timeline()

      const timeout = setTimeout(() => {
        const elements = document.querySelectorAll('.reveal-item')
        elements.forEach((el) => {
          createShowFromBottomAnimation(tl, el);
        })

        ScrollTrigger.sort()
        ScrollTrigger.refresh()
      }, 100)

      return () => {
        clearTimeout(timeout)
        tl.kill()
      }
    }
  }, [sections])

  return (
    <Layout title={title} metas={metas} isSideNavHidden isBottomMenuHidden={true}>
      <HeroSection
        translationContent={{
          subtitle: '',
          firstPartOfTitle: titlePart1,
          secondPartOfTitle: titlePart2,
          content: '',
          ctaLink: '',
          ctaText: '',
          sectionName: 'navigable-page-hero',
        }}
        isFullHeight={false}
        showBackButton={true}
        showIso={showIso}
        sectionClassName="mt-16 md:mt-colossal"
        isH2={true}
      />
      <div className="md:flex container pb-20 md:pb-32 md:gap-24 lg:gap-32">
        <div className="hidden md:block md:w-1/3 lg:w-1/4 mb-12 md:sticky self-start top-24 reveal-item opacity-0">
          <SideNavigation className="mb-8" />
        </div>
        <div className="flex-1">
          {sections.map(({ slug, title: sectionTitle, items }) => (
            <NavigableSection
              key={slug}
              slug={slug}
              title={sectionTitle}
              items={items}
              className="pb-16"
            />
          ))}
        </div>
      </div>
      <ContactSection />
    </Layout>
  )
}

export default NavigablePage
